import { FormControl, Grid, InputLabel, MenuItem, Paper, Select } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Season } from 'redux/models/season';
import { Badge } from 'redux/models/badge';
import { Team } from 'redux/models/team';
import { RootStore } from 'redux/store';
import { getTeamBadgesForSeason } from 'redux/thunks/teamBadges';
import { getTeamSeasons } from 'redux/thunks/user';
import moment from 'moment';

import './TeamBadges.scss';
import { getBadgeIcon, orderByProperty } from 'helpers';
import { Loader } from 'components/Loader';
import TeamBadgesTour from 'components/Tours/TeamBadgesTour';
import { withRouteInformation } from 'components/withRouteInformation';
interface FormattedBadge {
	name: string;
	value: string;
	recipients: string;
	icon: string;
	description: string;
}

interface TeamBadgesProps {
	pathname: string;
}

const TeamBadges: React.FC<TeamBadgesProps> = (props: TeamBadgesProps) => {
	const { pathname } = props;
	const userState = useSelector((state: RootStore) => state.user);
	const teamBadgesState = useSelector((state: RootStore) => state.teamBadges);
	const dispatch = useDispatch();
	const [selectedSeasonId, setSelectedSeasonId] = useState('');
	const [seasonDateRange, setSeasonDateRange] = useState('');
	const [currentTeamSeasons, setCurrentTeamSeasons] = useState<Season[]>([]);
	const [seasonBadges, setSeasonBadges] = useState<FormattedBadge[]>([]);
	const [hasLoaded, setHasLoaded] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isTourOpen, setIsTourOpen] = useState<boolean>(false);

	//  Whenever a new season is selected in the drop-down we need to go retrieve
	// the badges for the seasons
	const handleSeasonChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const seasonId = event.target.value as string;
		setSelectedSeasonId(seasonId);
		if (userState.selectedTeam?.teamId && seasonId) {
			calculateSeasonDateRange(seasonId);

			dispatch(getTeamBadgesForSeason(userState.selectedTeam?.teamId, seasonId));
			setIsLoading(true);
			setHasLoaded(false);
		}
	};

	// creates a date range string for the given season
	const calculateSeasonDateRange = useCallback(
		(seasonId: string) => {
			const team = userState.teams.find(team => team.teamId === userState.selectedTeam?.teamId) as Team;
			if (team && team.seasons && seasonId) {
				const season = team.seasons.find(s => s.seasonId === seasonId) as Season;
				const date = `${moment(season.seasonStartDate).format('MMMM DD, YYYY')} - ${
					season.hasOwnProperty('seasonEndDate') ? moment(season.seasonEndDate).format('MMMM DD, YYYY') : 'Present'
				}`;
				setSeasonDateRange(date);
			}
		},
		[userState.selectedTeam, userState.teams]
	);

	// get seasons for the selected team when the page loads
	useEffect(() => {
		if (userState.selectedTeam?.teamId) {
			dispatch(getTeamSeasons(userState.selectedTeam?.teamId));
			setIsLoading(true);
		}
	}, [userState.selectedTeam, dispatch]);

	// Get badges once seasons return for the default team
	useEffect(() => {
		if (userState.selectedTeam?.teamId) {
			const team = userState.teams.find(team => team.teamId === userState.selectedTeam?.teamId) as Team;
			if (team && team.seasons && team.seasons.length > 0) {
				let seasons = orderByProperty(team.seasons, 'seasonStartDate', true) as Season[];
				dispatch(getTeamBadgesForSeason(userState.selectedTeam?.teamId, seasons[0].seasonId));
				setIsLoading(true);
				setHasLoaded(false);
				setCurrentTeamSeasons(seasons);
				setSelectedSeasonId(seasons[0].seasonId);
				calculateSeasonDateRange(seasons[0].seasonId);
			} else {
				setCurrentTeamSeasons([]);
			}
		}
	}, [userState.selectedTeam, userState.teams, calculateSeasonDateRange, dispatch]);

	// util function to format a string of recipient names
	const getRecipientsString = (recipientArray: string[]) => {
		if (recipientArray.length < 2) {
			return recipientArray.length === 1 ? recipientArray[0] : '';
		} else if (recipientArray.length === 2) {
			return `${recipientArray[0]} & ${recipientArray[1]}`;
		} else {
			return recipientArray.join(', ');
		}
	};

	// Once the badges for a seasons have been recieved this function updates the loading state,
	// groups the badges, and formats the badges in a way that is easily rendered in the dom.
	useEffect(() => {
		const badges: Badge[] = teamBadgesState.selectedSeasonBadges;
		setIsLoading(teamBadgesState.badgesLoading);
		setHasLoaded(true);
		if (badges.length > 0) {
			const badgesObject: any = {};
			badges.forEach(badge => {
				if (badgesObject.hasOwnProperty(badge.badgeName)) {
					badgesObject[badge.badgeName].recipients.push(badge.userName);
				} else {
					badgesObject[badge.badgeName] = { badgeValue: badge.badgeValue, recipients: [badge.userName], badgeDescription: badge.badgeDescription };
				}
			});

			let formattedBadges = Object.entries(badgesObject).map(([key, value]: any) => {
				const badge: FormattedBadge = {
					name: key,
					value: value?.badgeValue,
					recipients: getRecipientsString(value?.recipients),
					icon: getBadgeIcon(key),
					description: value?.badgeDescription,
				};
				return badge;
			});

			formattedBadges = orderByProperty(formattedBadges, 'name') as FormattedBadge[];
			setSeasonBadges(formattedBadges);
		} else {
			setSeasonBadges([]);
		}
	}, [teamBadgesState]);

	// Use effect for managing if the tour should show or not
	useEffect(() => {
		if (userState.userId && pathname.includes('/details/badges') && !userState.tours.includes('TeamBadgesTour')) {
			setTimeout(function () {
				setIsTourOpen(true);
			}, 700);
		}
	}, [userState.tours, userState.userId, pathname]);

	return (
		<>
			<div>
				<Grid container direction='column' alignContent='center' alignItems='center' className='badge-season-container'>
					<Paper className='badges-paper' elevation={2}>
						<Grid container direction='column' justify='space-evenly' alignItems='center'>
							<h1>Season Badges</h1>

							{currentTeamSeasons && currentTeamSeasons.length > 0 && (
								<FormControl variant='outlined' className='season-drop-down'>
									<InputLabel id='season-select-outlined-label'>Season</InputLabel>
									<Select
										labelId='season-select-outlined-label'
										id='season-select-outlined'
										value={selectedSeasonId}
										onChange={handleSeasonChange}
										label='Season'
									>
										{currentTeamSeasons.map((season, index) => (
											<MenuItem key={index} value={season.seasonId}>
												{season.name.length > 0 ? season.name : '[current season]'}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
							<div className='season-date-range'>{seasonDateRange}</div>
						</Grid>

						<Grid container direction='row' justify='space-evenly' className='badge-wrapper'>
							{isLoading || !hasLoaded ? (
								<Loader />
							) : (
								seasonBadges.map((badge: FormattedBadge, index) => {
									return (
										<Grid item md={4} xs={12} key={index}>
											<div className='badge-container' title={badge.value}>
												<img src={badge.icon} alt='' className='badge-icon'></img>
												<div className='badge-name'>The {badge.name}</div>
												<div className='badge-description'>{badge.description}</div>
												<div className='badge-recipients'>{badge.recipients}</div>
											</div>
										</Grid>
									);
								})
							)}
						</Grid>
						{!isLoading && hasLoaded && seasonBadges.length < 1 && (
							<div className='no-badges'>No badges have been generated for this season. This season is still in progress.</div>
						)}
					</Paper>
				</Grid>
			</div>
			<TeamBadgesTour open={isTourOpen} onClose={() => setIsTourOpen(false)} />
		</>
	);
};

export default withRouteInformation(TeamBadges);
